/**
 * Dummy toolbar threschold value for 9 buttons. It is used as a placeholder in THRESHOLDS that would work only when
 * this value is overiden.
 */
export const DUMMY_9_BUTTONS_THRESHOLD_VALUE = Symbol('9_BUTTONS_THRESHOLD_VALUE');
/**
 * Dummy toolbar threschold value for 10 buttons. It is used as a placeholder in THRESHOLDS that would work only when
 * this value is overiden.
 */
export const DUMMY_10_BUTTONS_THRESHOLD_VALUE = Symbol('10_BUTTONS_THRESHOLD_VALUE');
export const DEFAULT_REDUCED_UI_MAIN_TOOLBAR_BUTTONS = ['microphone', 'camera'];
/**
 * 窄屏/手机底栏默认主按钮（不含 hangup / overflow）。
 * 对齐腾讯会议手机：静音、开启视频、共享屏幕、管理成员；录制/AI纪要进「更多」。
 * 可被 config.narrowMainToolbarButtons 覆盖。
 */
export const DEFAULT_NARROW_MAIN_TOOLBAR_BUTTONS = [
    'microphone',
    'camera',
    'desktop',
    'participants-pane'
];
/**
 * 8 钮档所需最小 videoSpaceWidth（不含举手 / 快捷输入）。
 * 须高于常见平板竖屏宽度（如 iPad Air 820）。
 */
export const MAIN_TOOLBAR_EIGHT_BUTTONS_WIDTH = 1100;
/**
 * 9 钮档（含举手 + 左侧快捷输入）所需最小 videoSpaceWidth。
 * 中文 labeled 底栏实测：1180–1280 仍会左栏（快捷输入+举手）溢出叠到 mic/cam；
 * 取 1400 留余量。低于此宽度不得显示快捷输入 / 主栏举手。
 */
export const MAIN_TOOLBAR_NINE_BUTTONS_WIDTH = 1400;
/**
 * 电脑端「应用」弹出层尺寸（对齐参考图，不含反应栏）。
 */
export const APPS_OVERFLOW_PANEL_WIDTH = 460;
export const APPS_OVERFLOW_PANEL_HEIGHT = 265;
/**
 * 「应用」面板左侧大图标宫格：只放溢出菜单里已有的应用感功能。
 * 没有的能力（美颜、问元宝、应用市场等）不造假入口。
 */
export const APPS_GRID_BUTTON_KEYS = [
    'select-background',
    'closedcaptions',
    'audiotranslation',
    'whiteboard'
];
/**
 * 手机「更多」抽屉宫格排序：对齐腾讯会议已有能力的位置，没有的能力不造假入口。
 */
export const MORE_DRAWER_GRID_ORDER = [
    'invite',
    'security',
    'chat',
    'filesharing',
    'recording',
    'closedcaptions',
    'audiotranslation',
    'polls',
    'select-background',
    'tileview',
    'pip',
    'settings',
    'ainotes',
    'livestreaming',
    'toggle-camera',
    'videoquality',
    'noisesuppression',
    'shareaudio',
    'sharedvideo',
    'stats',
    'profile',
    'shortcuts',
    'fullscreen',
    'highlight',
    'custom-panel'
];
/**
 * 手机「更多」抽屉不进宫格：头像项会破坏五列对齐。
 */
export const MORE_DRAWER_HIDDEN_KEYS = [
    'profile'
];
/**
 * 手机「更多」宫格短文案：避免长标签把五列撑歪。仅抽屉使用。
 */
export const MORE_DRAWER_SHORT_LABELS = {
    closedcaptions: 'toolbar.moreDrawerCaptions',
    noisesuppression: 'toolbar.moreDrawerNoiseSuppression',
    security: 'toolbar.moreDrawerSecurity',
    settings: 'toolbar.moreDrawerSettings',
    stats: 'toolbar.moreDrawerStats',
    tileview: 'toolbar.moreDrawerTileView',
    videoquality: 'toolbar.moreDrawerQuality'
};
/**
 * 手机「更多」抽屉「扩展应用」分区：只放已有的应用感入口。
 */
export const MORE_DRAWER_EXTENDED_APP_KEYS = [
    'whiteboard',
    'etherpad',
    'embedmeeting',
    'download',
    'help'
];
/**
 * 左侧宫格图标底色：走主题 CSS 变量，深色/浅色由 themes.web.ts 注入。
 */
export const APPS_GRID_ICON_COLORS = {
    'select-background': 'var(--apps-grid-icon-select-background)',
    audiotranslation: 'var(--apps-grid-icon-audiotranslation)',
    closedcaptions: 'var(--apps-grid-icon-closedcaptions)',
    whiteboard: 'var(--apps-grid-icon-whiteboard)'
};
/**
 * Thresholds for displaying toolbox buttons.
 * 宽度按「文案标签 + 左侧快捷输入」实测需求上调，覆盖平板中档（约 768–1024）。
 * 匹配规则为 clientWidth > width，取第一条命中档。
 *
 * 平板示例（iPad Air 竖屏 ≈820）：落在 6 钮档 —— 已收起快捷输入/举手，
 * 主栏保留 mic/cam/desktop/invite/participants/chat，避免重叠。
 */
export const THRESHOLDS = [
    // This entry won't be used unless the order is overridden trough the mainToolbarButtons config prop.
    {
        width: 1600,
        order: DUMMY_10_BUTTONS_THRESHOLD_VALUE
    },
    // 9 钮：含 raisehand；快捷输入与举手同档显示（可被 mainToolbarButtons 同长度数组覆盖）
    {
        width: MAIN_TOOLBAR_NINE_BUTTONS_WIDTH,
        order: DUMMY_9_BUTTONS_THRESHOLD_VALUE
    },
    {
        // >1100：8 钮，已收起举手与快捷输入
        width: MAIN_TOOLBAR_EIGHT_BUTTONS_WIDTH,
        order: ['microphone', 'camera', 'desktop', 'invite', 'participants-pane', 'chat', 'recording', 'ainotes']
    },
    {
        // 平板横屏偏宽：再收 AI纪要
        width: 960,
        order: ['microphone', 'camera', 'desktop', 'invite', 'participants-pane', 'chat', 'recording']
    },
    {
        // 平板竖屏（如 820）：再收录制 → 6 钮
        width: 800,
        order: ['microphone', 'camera', 'desktop', 'invite', 'participants-pane', 'chat']
    },
    {
        width: 700,
        order: ['microphone', 'camera', 'desktop', 'participants-pane', 'chat']
    },
    {
        // 接近窄屏切换前：主栏保留 mic/cam/desktop/participants
        width: 600,
        order: ['microphone', 'camera', 'desktop', 'participants-pane']
    },
    {
        width: 520,
        order: ['microphone', 'camera', 'participants-pane']
    },
    {
        width: 200,
        order: ['microphone', 'camera']
    }
];
/**
 * Thresholds for displaying native toolbox buttons.
 */
export const NATIVE_THRESHOLDS = [
    {
        width: 560,
        order: ['microphone', 'camera', 'chat', 'desktop', 'raisehand', 'tileview', 'overflowmenu', 'hangup']
    },
    {
        width: 500,
        order: ['microphone', 'camera', 'chat', 'raisehand', 'tileview', 'overflowmenu', 'hangup']
    },
    {
        width: 440,
        order: ['microphone', 'camera', 'chat', 'raisehand', 'overflowmenu', 'hangup']
    },
    {
        width: 380,
        order: ['microphone', 'camera', 'chat', 'overflowmenu', 'hangup']
    },
    {
        width: 320,
        order: ['microphone', 'camera', 'overflowmenu', 'hangup']
    }
];
/**
 * Main toolbar buttons priority used to determine which button should be picked to fill empty spaces for disabled
 * buttons.
 */
export const MAIN_TOOLBAR_BUTTONS_PRIORITY = [
    'microphone',
    'camera',
    'desktop',
    'invite',
    'participants-pane',
    'chat',
    'recording',
    'ainotes',
    'raisehand',
    'reactions',
    'tileview',
    'overflowmenu',
    'hangup',
    'toggle-camera',
    'videoquality',
    'fullscreen',
    'security',
    'closedcaptions',
    'livestreaming',
    'linktosalesforce',
    'sharedvideo',
    'shareaudio',
    'noisesuppression',
    'whiteboard',
    'etherpad',
    'select-background',
    'stats',
    'settings',
    'shortcuts',
    'profile',
    'embedmeeting',
    'feedback',
    'download',
    'help',
    'custom-panel'
];
export const TOOLBAR_TIMEOUT = 4000;
export const DRAWER_MAX_HEIGHT = '80dvh - 64px';
/**
 * {@link DRAWER_MAX_HEIGHT} 在不支持 dvh 的移动浏览器上的回退值。
 */
export const DRAWER_MAX_HEIGHT_FALLBACK = '80vh - 64px';
// Around 300 to be displayed above components like chat
export const ZINDEX_DIALOG_PORTAL = 302;
/**
 * Color for spinner displayed in the toolbar.
 */
export const SPINNER_COLOR = '#929292';
/**
 * The list of all possible UI buttons.
 *
 * @protected
 * @type Array<string>
 */
export const TOOLBAR_BUTTONS = [
    'ainotes',
    'audiotranslation',
    'camera',
    'chat',
    'closedcaptions',
    'custom-panel',
    'desktop',
    'download',
    'embedmeeting',
    'etherpad',
    'feedback',
    'filesharing',
    'fullscreen',
    'hangup',
    'help',
    'highlight',
    'invite',
    'linktosalesforce',
    'livestreaming',
    'microphone',
    'mute-everyone',
    'mute-video-everyone',
    'participants-pane',
    'pip',
    'polls',
    'profile',
    'raisehand',
    'recording',
    'security',
    'select-background',
    'settings',
    'shareaudio',
    'noisesuppression',
    'sharedvideo',
    'shortcuts',
    'stats',
    'tileview',
    'toggle-camera',
    'videoquality',
    'whiteboard'
];
/**
 * The list of all possible native buttons.
 *
 * @protected
 * @type Array<string>
 */
export const NATIVE_TOOLBAR_BUTTONS = [
    'camera',
    'chat',
    'hangup',
    'microphone',
    'overflowmenu',
    'raisehand',
    'desktop',
    'tileview'
];
/**
 * The toolbar buttons to show when in visitors mode.
 */
export const VISITORS_MODE_BUTTONS = [
    'chat',
    'closedcaptions',
    'fullscreen',
    'hangup',
    'participants-pane',
    'raisehand',
    'settings',
    'stats',
    'tileview',
    'videoquality'
];
